import { useEffect, useState } from 'react';
import { PenLine, Type, Fingerprint, ShieldCheck, Download, Loader2, BadgeCheck, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { signatures } from '../lib/api';
import { useAuth } from '../lib/auth';
import { biometricAvailable, createPasskey, getPasskey } from '../lib/webauthn';
import SignaturePad from './SignaturePad';
import FaceCapture from './FaceCapture';
import { fmtStamp } from './LiveClock';

const ROLES = [
  { role: 'seller', label: 'Seller (Factory)' },
  { role: 'buyer', label: 'Buyer' },
];

export default function PoSignaturePanel({ po, iAmFactory, iAmBuyer, onSigned }) {
  const sigs = po.signatures || {};
  const myRoles = ROLES.filter((r) => (r.role === 'seller' ? iAmFactory : iAmBuyer) && !sigs[r.role]);
  return (
    <div data-testid="po-signature-panel" className="mt-6 rounded-xl border-2 border-[#0D47A1]/20 bg-[#F5F9FF] p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="w-4 h-4 text-[#0D47A1]"/>
        <div className="font-heading font-bold text-base text-[#0D47A1]">Digital signatures</div>
        <div className="ml-auto flex items-center gap-2">
          {Object.keys(sigs).length > 0 && <ShareWhatsApp po={po}/>}
          {po.pdf_url && <DownloadPdf url={po.pdf_url} ordId={po.ord_id}/>}
        </div>
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-2">
        {ROLES.map(({ role, label }) => <SignatureStamp key={role} role={role} label={label} sig={sigs[role]}/>)}
      </div>
      {myRoles.length > 0 && <SignForm key={Object.keys(sigs).sort().join(',')} po={po} roles={myRoles} onSigned={onSigned}/>}
    </div>
  );
}

function SignatureStamp({ role, label, sig }) {
  return (
    <div data-testid={`po-sig-${role}`} className={`rounded-lg border p-3 bg-white ${sig ? 'border-[#1B5E20]/40' : 'border-dashed border-ink/20'}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/50">{label}</div>
      {sig ? (
        <>
          <div className="font-bold text-sm mt-1 flex items-center gap-1"><BadgeCheck className="w-4 h-4 text-[#1B5E20]"/>{sig.name}</div>
          <div className="text-[11px] text-ink/60">{fmtStamp(sig.signed_at, true)} · {sig.methods.join(' + ')}</div>
          <div data-testid={`po-sig-${role}-hash`} className="font-mono text-[9px] text-ink/50 break-all mt-1">SHA-256 {sig.hash}</div>
        </>
      ) : <div className="text-xs text-ink/50 mt-1">Not signed yet</div>}
    </div>
  );
}

function DownloadPdf({ url, ordId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await signatures.fileBlob(url);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${ordId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Could not download PDF'); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="po-sig-download-pdf" onClick={download} disabled={busy} className="pill !bg-white text-xs">
      {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Download className="w-3 h-3"/>}PDF
    </button>
  );
}

function ShareWhatsApp({ po }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const r = await signatures.share(po.ord_id || po.id);
      const link = `${window.location.origin}/po/verify/${r.token}`;
      const text = [
        `Namaste${r.to_name ? ` ${r.to_name}` : ''}`,
        `Purchase Order *${r.ord_id}* (Rs ${Math.round(r.subtotal || 0)}) is digitally signed by ${r.signed_roles.join(' & ')}.`,
        `View, verify & download the signed PDF: ${link}`,
        `Link valid till ${new Date(r.expires_at).toLocaleDateString('en-IN')} - BharatCart`,
      ].join('\n');
      const url = `https://wa.me/${r.to_phone || ''}?text=${encodeURIComponent(text)}`;
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      win?.close();
      toast.error(e?.response?.data?.detail || 'Could not create share link');
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="po-sig-share-whatsapp" onClick={share} disabled={busy}
      className="pill !bg-[#25D366] !text-white !border-[#25D366] text-xs">
      {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Share2 className="w-3 h-3"/>}WhatsApp
    </button>
  );
}

function SignForm({ po, roles, onSigned }) {
  const { user } = useAuth();
  const [role, setRole] = useState(roles[0].role);
  const [method, setMethod] = useState('drawn');
  const [profile, setProfile] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [typed, setTyped] = useState(user?.name || '');
  const [face, setFace] = useState(null);
  const [useBio, setUseBio] = useState(false);
  const [bioOk, setBioOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { signatures.me().then(setProfile).catch(() => {}); biometricAvailable().then(setBioOk); }, []);

  const linkDevice = async () => {
    const r = await signatures.passkeyRegisterVerify(await createPasskey(await signatures.passkeyRegisterOptions()));
    setProfile((p) => ({ ...p, passkeys: r.passkeys }));
    toast.success('Fingerprint / face unlock linked to BharatCart');
  };
  const onLink = () => linkDevice().catch((e) => toast.error(
    e?.name === 'InvalidStateError' ? 'This device is already linked' : (e?.response?.data?.detail || 'Could not link this device')));

  const runBiometric = async () => {
    if (!profile?.passkeys) await linkDevice();
    return getPasskey(await signatures.poPasskeyOptions(po.ord_id || po.id, role));
  };

  const sign = async () => {
    setBusy(true);
    try {
      if (method === 'drawn' && drawing) {
        await signatures.save({ name: typed.trim() || user?.name || 'Signer', drawn_png_b64: drawing });
      } else if (method === 'drawn' && !profile?.has_drawn) {
        toast.error('Please draw your signature'); return;
      }
      const passkey_credential = useBio ? await runBiometric() : null;
      const r = await signatures.signPo(po.ord_id || po.id, {
        method, role, typed_name: method === 'typed' ? typed : null, face_png_b64: face, passkey_credential,
      });
      toast.success(`Signed as ${role} · ${r.signature.methods.join(' + ')}`);
      onSigned?.();
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Biometric check cancelled — if this is a new phone, tap “Link this device” first'
        : (e?.response?.data?.detail || e?.message || 'Could not sign');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="po-sign-form" className="mt-4 rounded-lg bg-white border border-ink/10 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-bold text-sm">Sign this PO as</div>
        {roles.map((r) => (
          <button key={r.role} data-testid={`po-sign-role-${r.role}`} onClick={() => setRole(r.role)}
            className={`pill text-xs ${role === r.role ? '!bg-ink !text-white' : '!bg-white'}`}>{r.label}</button>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="po-sign-method-drawn" onClick={() => setMethod('drawn')} className={`pill text-xs ${method === 'drawn' ? '!bg-[#0D47A1] !text-white' : '!bg-white'}`}><PenLine className="w-3 h-3"/>Draw</button>
        <button data-testid="po-sign-method-typed" onClick={() => setMethod('typed')} className={`pill text-xs ${method === 'typed' ? '!bg-[#0D47A1] !text-white' : '!bg-white'}`}><Type className="w-3 h-3"/>Type name</button>
      </div>
      <div className="mt-3">
        {method === 'drawn' ? (
          <>
            {profile?.has_drawn && !drawing && (
              <div className="mb-2 flex items-center gap-2 text-xs text-ink/60" data-testid="po-sign-saved-signature">
                <img src={profile.drawn_png_b64} alt="Saved signature" className="h-10 bg-[#FAFAF7] rounded border border-ink/10 px-2"/>
                Saved signature will be used — or draw a new one below.
              </div>
            )}
            <SignaturePad onChange={setDrawing} testid="po-sign-pad"/>
          </>
        ) : (
          <input data-testid="po-sign-typed-name" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Your full name"
            className="w-full border-2 border-ink/15 rounded-lg px-3 h-11 text-lg outline-none focus:border-ink" style={{ fontFamily: 'cursive' }}/>
        )}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] font-black text-ink/50">Optional extra proof</div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <FaceCapture value={face} onChange={setFace}/>
        {bioOk ? (
          <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer" data-testid="po-sign-biometric-label">
            <input type="checkbox" data-testid="po-sign-biometric" checked={useBio} onChange={(e) => setUseBio(e.target.checked)}/>
            <Fingerprint className="w-4 h-4 text-[#0D47A1]"/>Confirm with fingerprint / face unlock
          </label>
        ) : <span data-testid="po-sign-biometric-unavailable" className="text-[11px] text-ink/60">Fingerprint unlock not available on this device</span>}
        {bioOk && (
          <button type="button" data-testid="po-sign-biometric-link" onClick={onLink} className="text-[11px] font-bold text-[#0D47A1] underline">
            Link this device
          </button>
        )}
      </div>
      <button data-testid="po-sign-submit" onClick={sign} disabled={busy} className="btn-primary w-full mt-4 !py-2.5 text-sm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>}Sign PO digitally
      </button>
      <p className="text-[10px] text-ink/50 mt-2">Your signature, time and a SHA-256 tamper-proof code are stamped on the PDF. Signatures cannot be changed later.</p>
    </div>
  );
}
